/**
 * ONE way to fold a food name (#1048).
 *
 * Three existed, and every disagreement between them was a wrong or a missed
 * product match:
 *
 * - `normalizeCatalogName` (groceryCatalog.ts) — NFKD accent fold, plural `s`
 *   strip with a not-`ss` guard. No `&`→"and", no irregular plurals.
 * - `normalizeFoodIdentity` (mealAssembler.ts) — `&`→"and", `-ies`/`-es`/`-s`
 *   stripping with overrides. No accent fold, and no `ss` guard, so "glass"
 *   folded to "glas".
 * - `normalizeFoodName` (systemRecipeCatalogSeed.ts) — strips prep words, keeps
 *   every plural, keeps accents.
 *
 * The merge takes every rule that was right somewhere and applies it
 * everywhere. TWO functions, because there are genuinely two questions:
 *
 * - `normalizeFoodIdentity` — "is this literally the same food?" Descriptors
 *   are KEPT, so "fresh mozzarella" and "mozzarella" stay apart. Use it
 *   wherever a mismatch would attribute one food's data to another (#697).
 * - `normalizeFoodMatch` — "would a shopper reach for this?" Descriptors are
 *   DROPPED, so "2 cans of diced tomatoes" finds "Tomatoes". Deliberately
 *   lossy, and wrong for anything that must not conflate two foods.
 *
 * Nothing here knows about products, households or catalogs; it is string
 * folding, and it stays that way so every caller can share it.
 */
/**
 * Preparation and packaging words a MATCH may ignore. Not an identity concern:
 * "fresh tomatoes" and "canned tomatoes" are different foods to buy and the
 * same food to cook with.
 */
const STOP_WORDS = new Set([
    "fresh",
    "frozen",
    "canned",
    "can",
    "jar",
    "jarred",
    "box",
    "bag",
    "dried",
    "dry",
    "whole",
    "sliced",
    "diced",
    "chopped",
    "minced",
    "shredded",
    "grated",
    "ground",
    "boneless",
    "skinless",
    "large",
    "small",
    "medium",
    "low",
    "reduced",
    "fat",
    "organic"
]);
/**
 * Plurals the rules below get wrong.
 *
 * The former mealAssembler list mapped "beans", "greens" and "fries" to
 * THEMSELVES, on the reasoning that nobody shops for a bean. That reasoning
 * costs more than it buys: a self-mapping override makes the plural and the
 * singular disagree, so "Garbanzo Beans" and "garbanzo bean" stopped being one
 * key — exactly the failure this whole change exists to remove. They are folded
 * like every other plural now; the key reads "bean", and both spellings reach
 * it. (Verified against the catalogs by the diff harness: no new cross-item
 * merges and no splits.)
 */
const SINGULAR_OVERRIDES = new Map([
    ["tomatoes", "tomato"],
    ["potatoes", "potato"],
    ["tortillas", "tortilla"],
    ["berries", "berry"],
    ["eggs", "egg"],
    ["nuggets", "nugget"],
    ["tots", "tot"],
    ["patties", "patty"],
    // Singular words that LOOK like the plurals the rules below strip. Added
    // alongside the "-sses" rule this change introduces, so that rule cannot
    // turn a real food into a token that matches nothing ("molass").
    //
    // Self-mapping is safe HERE and was not safe for "beans" (see above), and the
    // difference is worth stating: these words have no second form in use. Nobody
    // writes "asparaguses", so the plural and the singular can never disagree.
    // "beans" had a real singular to disagree with, which is exactly how it broke.
    ["molasses", "molasses"],
    ["watercress", "watercress"],
    ["couscous", "couscous"],
    ["hummus", "hummus"],
    // Plain "-us" nouns that the bare "-s" strip truncates: produce.asparagus is a
    // shipped catalog entry, and "citrus" appears 11 times across the corpora.
    // The key is stable either way (both spellings mangle identically, so matching
    // still works), but this fold is ALSO the literal USDA FoodData Central query
    // string in nutrition.ts — which was searching for "asparagu".
    ["asparagus", "asparagus"],
    ["citrus", "citrus"],
    // Irregular plurals, as OVERRIDES rather than rules — deliberately, and the
    // corpora say why. Every "-oes"/"-ves" word in the grocery catalog and the
    // recipe corpus was counted before choosing: a blanket "-ves" -> "-f" rule
    // would have to mangle cloves (344 occurrences), olives (32), chives (22),
    // gloves (9) and preserves (2) — every one of which the plain "-s" rule
    // already gets right — in order to fix the three below. A blanket "-oes"
    // rule has the same shape of problem with "shoes" and "toes". These four are
    // the entire real population; a rule would be a far bigger blast radius for
    // no more reach.
    ["leaves", "leaf"],
    ["halves", "half"],
    ["knives", "knife"],
    // "mangoes" is the one this change REGRESSED rather than merely failed to
    // fix. The old mealAssembler stripped "-es" from anything long enough, so it
    // produced "mango"; restricting "-es" to sibilants (so "grapes" stops becoming
    // "grap") dropped it on the floor, leaving "mangoes" -> "mangoe" while
    // "Mango" -> "mango". What that costs, stated accurately: a household product
    // or ingredient NAMED "Mangoes" — and any line whose parsed name is the food,
    // like "2 mangoes" or "1/2 Mango" — stops reaching the catalog's Mango.
    // (An earlier version of this comment cited the shipped Mango Sticky Rice
    // recipe. That was wrong and is worth recording as wrong: its line is "2 ripe
    // mangoes, peeled and sliced", whose stored key is "ripe mango peeled and",
    // and that resolved to nothing before this change and resolves to nothing
    // after. The override is right; the story was not.)
    ["mangoes", "mango"],
    // The "-ies" -> "-y" rule below is correct for 43 of the 48 "-ies" words in
    // the corpora (berries, fries, pastries, anchovies, patties...), and wrong for
    // exactly the class whose SINGULAR already ends in "-ie". Counted before
    // choosing, as with "-oes"/"-ves": cookies x181 (vs cookie x58), veggies x22
    // (veggie x18), brownies x17 (brownie x3), calories x2, cuties x1. Overrides
    // again rather than a rule, because no spelling test separates "cookies" from
    // "berries" — the difference lives in the singular, which we do not have.
    //
    // This one is a REGRESSION against the catalog normalizer specifically, which
    // stripped a single "s" and so got every one of these right. It is the exact
    // failure this merge was meant to prevent, and the resolution gate could not
    // see it: that gate compares against the frozen mealAssembler fold, which
    // carried the identical "-ies" rule, so old and new agreed and it stayed green.
    ["cookies", "cookie"],
    ["veggies", "veggie"],
    ["brownies", "brownie"],
    ["calories", "calorie"],
    ["cuties", "cutie"],
    // "krispies" is the one where BOTH stems are real foods in the corpus
    // (krispie x2 from Rice Krispies, krispy x4 from Krispy Kreme). Without the
    // override the rule folds them together, and a merge is worse than a split.
    ["krispies", "krispie"],
    // Same shape one ending over: "-ches" is right for 17 of the 18 "-ches" words
    // (batches, inches, peaches, sandwiches...) and wrong only where the singular
    // ends in "-che". "leche" appears 71 times (Dulce de Leche, Tres Leches Cake,
    // Arroz con Leche) against 8 for "leches", so the rule stays and this doesn't.
    ["leches", "leche"],
    // Not in any corpus today, unlike every entry above — these three are the
    // other FOODS in the "-che" class, and they cost nothing (0 corpus outputs
    // change) while the class is demonstrably broken. A household typing "quiches"
    // as a product name is a likelier event than a new "-ches" word arriving that
    // wants the rule.
    ["quiches", "quiche"],
    ["brioches", "brioche"],
    ["ganaches", "ganache"]
]);
/**
 * Endings where the plural really is "-es" rather than a bare "-s" on a word
 * that happens to end in "e".
 *
 * This is the one rule the merge had to INVENT rather than inherit. The
 * mealAssembler normalizer stripped "-es" from anything long enough, so
 * "grapes" became "grap" and "apples" became "appl"; the catalog normalizer
 * only ever stripped one "s", so "boxes" stayed "boxe". Restricting "-es" to
 * the sibilant endings that actually take it gets both right.
 *
 * "zes" was NARROWED to "zzes", which is the only form that is right in both
 * directions. Every "-zes" word in the corpora has a "-ze" singular —
 * tenderizes, caramelizes, freezes, seizes, sizes, prizes, gelatinizes — so the
 * broad rule turned "sizes" into "siz" while "size" (x162) stayed itself.
 * Dropping it outright was the first attempt and was also wrong: "fizzes" and
 * "buzzes" genuinely need the "-es" strip, and the plain "-s" fallback gives
 * "fizze". Requiring the doubled z separates the two populations exactly:
 *
 *   fizzes  -> fizz    buzzes -> buzz     (doubled z: strip "es")
 *   sizes   -> size    freezes -> freeze  (single z: strip "s")
 *
 * ("quizzes" -> "quizz" rather than "quiz" either way. Unchanged by this, and
 * not a food.) The other four endings are each right for every corpus word they
 * touch, bar the "-ches" exception handled by an override above.
 */
const ES_PLURAL_ENDINGS = ["sses", "shes", "ches", "xes", "zzes"];
function singularizeWord(word) {
    const override = SINGULAR_OVERRIDES.get(word);
    if (override)
        return override;
    if (word.length > 4 && word.endsWith("ies"))
        return `${word.slice(0, -3)}y`;
    if (word.length > 4 && ES_PLURAL_ENDINGS.some((ending) => word.endsWith(ending)))
        return word.slice(0, -2);
    // The `ss` guard is what keeps "glass" from becoming "glas" and "grass" from
    // becoming "gras" — a real defect in the mealAssembler normalizer, which had
    // no such guard.
    if (word.length > 3 && word.endsWith("s") && !word.endsWith("ss"))
        return word.slice(0, -1);
    return word;
}
function foldFoodWords(value, dropDescriptors) {
    const lowered = String(value ?? "")
        .toLowerCase()
        // Vulgar fractions go BEFORE the NFKD pass, and that order is the whole
        // point: NFKD decomposes "½" into "1⁄2", so normalizing first leaves the
        // digits behind and "½ Red Onions" folds to "1 2 red onion" instead of
        // "red onion". All three old normalizers stripped these characters as
        // ordinary punctuation and never had the problem; this one introduced it by
        // adding NFKD. A quantity is not part of a food's identity either way.
        // The range runs to U+215F (⅟), not U+215E, and includes the superscript
        // and subscript digit blocks: "¹⁄₂" is not one precomposed glyph but three
        // characters, and NFKD turns the two survivors into "1" and "2" just the
        // same. Zero corpus occurrences today — this is guarding the shape of the
        // bug, not a live instance of it.
        .replace(/[¼-¾⅐-⅟⁄¹²³⁰-⁹₀-₉]/g, " ")
        // NFKD + combining-mark strip, so "jalapeño" and "jalapeno" are one food.
        // Only the catalog normalizer did this; the other two left accented names
        // permanently unmatchable against their unaccented spellings.
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        // Before punctuation collapses, or "mac & cheese" becomes "mac cheese"
        // while "mac and cheese" stays three words.
        .replace(/&/g, " and ")
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
    if (!lowered)
        return "";
    const words = lowered
        .split(" ")
        .filter((word) => word && !(dropDescriptors && STOP_WORDS.has(word)))
        .map(singularizeWord);
    return words.join(" ");
}
/**
 * IDENTITY: the same food, however it was typed. Descriptors are kept.
 *
 * "Tomatoes", "tomato" and "TOMATOES!" are one key; "fresh tomatoes" and
 * "canned tomatoes" are three different ones.
 */
export function normalizeFoodIdentity(value) {
    return foldFoodWords(value, false);
}
/**
 * MATCHING: the food a recipe line is reaching for. Descriptors are dropped.
 *
 * Deliberately lossy — "fresh tomatoes" and "canned tomatoes" both land on
 * "tomato" — which makes it the wrong key for anything that must not conflate
 * two foods. Price quoting, duplicate detection and catalog identity all want
 * `normalizeFoodIdentity`.
 */
export function normalizeFoodMatch(value) {
    return foldFoodWords(value, true);
}
/** Exposed for the diff harness and for tests that assert the word lists. */
export const foodIdentityStopWords = STOP_WORDS;
export const foodIdentitySingularOverrides = SINGULAR_OVERRIDES;
